import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getSubscriptionStatus, SubscriptionType, SubscriptionResponse, isProPlan, isPlusPlan, isEnterprisePlan } from '../../services/subscriptionService';
import { generatePaymentLink } from '../../services/userService';

interface SubscriptionGuardProps {
  children: React.ReactNode;
  requiredPlans: SubscriptionType[];
  featureName: string;
}

const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({
  children,
  requiredPlans,
  featureName
}) => {
  const { currentUser } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!currentUser?.uid) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await getSubscriptionStatus(currentUser.uid);
      setSubscriptionData(response);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      setError('Failed to load subscription details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  const handleUpgrade = async (planType: "Plus" | "Pro" | "Topup" = 'Pro') => {
    if (!currentUser?.uid) return;

    try {
      setPaymentLoading(true);
      const { paymentLink } = await generatePaymentLink(planType, currentUser.uid, 0);
      window.open(paymentLink, '_blank');
    } catch (error) {
      console.error('Failed to generate payment link:', error);
      setError('Failed to generate payment link. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const isExpired = (endingTimestamp?: string) => {
    if (!endingTimestamp) return false;
    const expirationDate = new Date(endingTimestamp);
    const today = new Date();
    return expirationDate < today;
  };

  const getSubscriptionDisplayName = (type: string) => {
    if (!type) return 'Free Plan';

    // Handle new API format like "plus_4months", "pro_yearly", etc.
    const planType = type.split('_')[0]; // Extract part before underscore

    switch (planType.toLowerCase()) {
      case 'pro':
        return 'Pro Plan';
      case 'plus':
        return 'Plus Plan';
      case 'enterprise':
        return 'Enterprise Plan';
      case 'free':
      default:
        return 'Free Plan';
    }
  };

  // Check if user has required subscription
  const hasRequiredPlan = () => {
    if (!subscriptionData) return false;
    return requiredPlans.some(plan => {
      const planType = subscriptionData.type;
      switch (plan) {
        case SubscriptionType.PRO:
          return isProPlan(planType);
        case SubscriptionType.PLUS:
          return isPlusPlan(planType);
        case SubscriptionType.ENTERPRISE:
          return isEnterprisePlan(planType);
        default:
          return planType.toLowerCase() === plan;
      }
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-500 mx-auto mb-4"></div>
          <p className="text-text-description">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Subscription</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchSubscriptionStatus}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // User has required plan - show the protected content
  if (hasRequiredPlan()) {
    return <>{children}</>;
  }

  // Determine minimum required plan
  const includesPlus = requiredPlans.includes(SubscriptionType.PLUS);
  const minPlanName = includesPlus ? 'Plus' : 'Pro';
  const plansList = includesPlus ? 'Plus, Pro and Enterprise' : 'Pro and Enterprise';

  // User doesn't have required plan - show upgrade prompt
  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-sm border border-accent-taupe overflow-hidden">
        {/* Header with feature info */}
        <div className="bg-gradient-to-r from-secondary-500 to-secondary-600 text-white p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <i className="fas fa-crown text-3xl"></i>
          </div>
          <h1 className="text-3xl font-bold mb-2">{featureName}</h1>
          <p className="text-secondary-100 text-lg">Premium Feature - {minPlanName} Plan Required</p>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-text-heading mb-4">
              Upgrade to {minPlanName} Plan to Access {featureName}
            </h2>
            <p className="text-text-description mb-2">
              You're currently on the <span className="font-semibold text-secondary-600">
                {subscriptionData ?
                  (subscriptionData.endingTimestamp && isExpired(subscriptionData.endingTimestamp) && subscriptionData.previous_type
                    ? getSubscriptionDisplayName(subscriptionData.previous_type) + ' (Expired)'
                    : getSubscriptionDisplayName(subscriptionData.type))
                  : 'Free Plan'}
              </span>
            </p>
            <p className="text-text-description">
              {featureName} is available for {plansList} plan subscribers.
            </p>
          </div>

          {/* Pro Plan Benefits */}
          <div className="bg-accent-sand rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-text-heading mb-4 text-center">
              Pro Plan Benefits
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <i className="fas fa-check-circle text-green-500"></i>
                <span className="text-text-heading">Unlimited AI Photoshoot (40 credits/month)</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="fas fa-check-circle text-green-500"></i>
                <span className="text-text-heading">Premium Try-On Features</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="fas fa-check-circle text-green-500"></i>
                <span className="text-text-heading">AI Designer Access</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="fas fa-check-circle text-green-500"></i>
                <span className="text-text-heading">Canvas Editor & Video Generation</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="fas fa-check-circle text-green-500"></i>
                <span className="text-text-heading">Unlimited Gallery Storage</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="fas fa-check-circle text-green-500"></i>
                <span className="text-text-heading">24/7 Priority Support</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="fas fa-check-circle text-green-500"></i>
                <span className="text-text-heading">Outfit Extractor (Flatlay)</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {includesPlus && (
              <button
                onClick={() => handleUpgrade('Plus')}
                disabled={paymentLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {paymentLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-star"></i>
                    <span>Upgrade to Plus</span>
                  </>
                )}
              </button>
            )}
            
            <button
              onClick={() => handleUpgrade('Pro')}
              disabled={paymentLoading}
              className="bg-secondary-500 hover:bg-secondary-600 text-white px-8 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {paymentLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-crown"></i>
                  <span>Upgrade to Pro</span>
                </>
              )}
            </button>

            <Link
              to="/subscription"
              className="bg-accent-sand hover:bg-accent-taupe text-text-heading px-8 py-3 rounded-lg font-medium transition-colors text-center flex items-center justify-center space-x-2"
            >
              <i className="fas fa-info-circle"></i>
              <span>View All Plans</span>
            </Link>
          </div>

          {/* Additional Info */}
          <div className="mt-8 pt-6 border-t border-accent-taupe text-center">
            <p className="text-sm text-text-description">
              Questions about upgrading?{' '}
              <a
                href="mailto:manish.sahu@snapmydesign.com?subject=Pro Plan Upgrade Question"
                className="text-secondary-600 hover:text-secondary-700 font-medium"
              >
                Contact our support team
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionGuard; 